import { useCallback, useState } from 'react';
import {
  IconCalendarOutlined,
  IconInfoOutlined,
  Lozenge,
  SidebarPanel,
  useDeviceDetection,
} from '@gafe/common-ui';
import merge from 'classnames';

import { SideBarTextContent } from '@/components/shared/TasksSection/components/SideBarTextContent/SideBarTextContent';
import { MediaType } from '@/types';

import styles from './ContentCard.module.scss';

export enum HOGuaranteeTypes {
  Active = 'Active',
  Void = 'Void',
}

export type LeanForwardStatusType = {
  leanForwardInformation?: {
    isLeanForward?: boolean;
    leanForwardDaysCount?: number | string | null;
    leanForwardHomeOwnerGuarantee?: 'Active' | 'Void' | null;
  };
  scrollToMessageBoard: (() => void) | undefined;
};

export const LeanForwardStatus = ({
  leanForwardInformation,
  scrollToMessageBoard,
}: LeanForwardStatusType) => {
  const mediaType = useDeviceDetection();
  const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);
  const isProjectLeanForward = leanForwardInformation?.isLeanForward;
  const guaranteeStillAvailable = Number(leanForwardInformation?.leanForwardDaysCount) <= 100;

  const homeOwnerGuaranteeSection = () => {
    if (
      !isProjectLeanForward ||
      leanForwardInformation?.leanForwardHomeOwnerGuarantee === HOGuaranteeTypes.Void
    ) {
      return (
        <Lozenge classes={{ container: styles.lozenge }} status="disabled" style="default">
          {isProjectLeanForward ? 'Void' : 'Ineligible'}
        </Lozenge>
      );
    }

    if (leanForwardInformation?.leanForwardHomeOwnerGuarantee === HOGuaranteeTypes.Active) {
      return guaranteeStillAvailable ? (
        <>
          {mediaType !== MediaType.Mobile && <p>Days Since Project Start</p>}
          <Lozenge
            classes={{ container: styles.lozenge }}
            prependIcon={<IconCalendarOutlined />}
            status="information"
            style="bold"
          >
            {leanForwardInformation?.leanForwardDaysCount}
          </Lozenge>
        </>
      ) : (
        <Lozenge classes={{ container: styles.lozenge }} status="warning" style="default">
          Under Review
        </Lozenge>
      );
    }

    return null;
  };

  const openDrawerHandler = useCallback(() => {
    setIsSideBarOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setIsSideBarOpen(false);
  }, []);

  const handleRedirectionToMessageBoard = useCallback(() => {
    scrollToMessageBoard?.();
    setIsSideBarOpen(false);
  }, [scrollToMessageBoard]);

  return (
    <>
      <div
        className={merge(
          styles.leanforward_card,
          { [styles.disabled]: !isProjectLeanForward },
          styles.leanforward_long_content,
        )}
      >
        <div className={styles.leanforward_card_general_section}>
          <p>100-Day Guarantee</p>
          <button onClick={openDrawerHandler} type="button">
            <IconInfoOutlined colorSchema="information" />
          </button>
        </div>

        <div className={styles.leanforward_card_homeowner_guarantee}>
          {homeOwnerGuaranteeSection()}
        </div>
      </div>
      <SidebarPanel
        classes={
          isSideBarOpen
            ? { container: styles.side_bar, wrapper: styles.side_wrapper_open }
            : { container: styles.side_bar, wrapper: styles.side_wrapper }
        }
        isOpen={isSideBarOpen}
        onClose={handleClosePanel}
      >
        <SideBarTextContent
          handleClose={handleClosePanel}
          scrollToMessageBoard={handleRedirectionToMessageBoard}
        />
      </SidebarPanel>
    </>
  );
};
